import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

/**
 * Global, persisted display preferences: chosen display currency and measurement unit.
 *
 * `currency`/`unit` are null when the visitor hasn't chosen one, in which case each
 * listing renders in its own native currency/unit (unchanged behaviour). A selection is
 * persisted to localStorage and applied across the whole site.
 *
 * SSR-safe: initial render uses null (native) on both server and first client paint;
 * the stored value is read in an effect after mount, so there is no hydration mismatch.
 */
const PreferencesContext = createContext({
  currency: null,
  setCurrency: () => {},
  unit: null,
  setUnit: () => {},
});

const CURRENCY_KEY = "pref_currency";
const UNIT_KEY = "pref_unit";

const readStored = (key) => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStored = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch (e) {
    /* ignore (private mode / disabled storage) */
  }
};

export const PreferencesProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(null);
  const [unit, setUnitState] = useState(null);

  // Hydrate from storage after mount (never during render → no hydration mismatch).
  useEffect(() => {
    const c = readStored(CURRENCY_KEY);
    const u = readStored(UNIT_KEY);
    if (c) setCurrencyState(c);
    if (u) setUnitState(u);
  }, []);

  const setCurrency = useCallback((code) => {
    setCurrencyState(code || null);
    writeStored(CURRENCY_KEY, code || null);
  }, []);

  const setUnit = useCallback((u) => {
    setUnitState(u || null);
    writeStored(UNIT_KEY, u || null);
  }, []);

  return (
    <PreferencesContext.Provider value={{ currency, setCurrency, unit, setUnit }}>
      {children}
    </PreferencesContext.Provider>
  );
};

PreferencesProvider.propTypes = {
  children: PropTypes.node,
};

export const usePreferences = () => useContext(PreferencesContext);

export default PreferencesContext;
